import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { JSX } from '@gov-design-system-ce/components';
import { useExtraConfigContext } from '@gov-nx/core/app';
import { useLocale } from '@gov-nx/core/service';
import {
	DataBoxConfirmModalType,
	DataBoxFolderType,
	isDataBoxTypePO,
	useDataBoxMessageDetailContext,
} from '@gov-nx/module/data-box';
import { PageCode } from '@gov-nx/module/page';
import type { FormButtonProps } from '@gov-nx/ui/web';
import { pageRouteResolver } from '@gov-nx/web';
import { useConnectButtonDefinition } from '../../Page/moje-podani/detail/ConnectToAnotherSubmission';
import { useDisconnectButtonDefinition } from '../../Page/moje-podani/detail/DisconnectFromSubmission';
import type { ButtonDropdownProps } from './ButtonDropdown';
import { ButtonDropdown } from './ButtonDropdown';

const getIconDefinition = (
	name: string,
	type: string,
	size?: JSX.GovIcon['size'],
	color?: JSX.GovIcon['color']
): JSX.GovIcon => ({
	name,
	type,
	color,
	size,
});

const getButtonDefinition = (props: FormButtonProps): FormButtonProps => props;

export const MessageActionButton = () => {
	const { t } = useLocale(PageCode['datove-schranky']);

	const {
		dataBox,
		dataBoxId,
		folderType,
		isLoading,
		loadingItems,
		message,
		messageOperations: {
			handleArchiveMessage,
			handleRestoreMessage,
			handleDownloadMessage,
			isArchivingEnabled,
		},
		setConfirmModal,
		mySubmissionConnection,
	} = useDataBoxMessageDetailContext();

	const { extraConfig } = useExtraConfigContext();

	const navigate = useNavigate();

	const redirect = () => {
		navigate(
			pageRouteResolver('datove-schranky.schranka', {
				dataBoxId,
				folderType,
			})
		);
	};

	const getConnectButton = useConnectButtonDefinition();
	const getDisconnectButton = useDisconnectButtonDefinition();

	const AnotherSubmissionButtons: ButtonDropdownProps['buttons'] =
		message?.datovaZpravaId
			? mySubmissionConnection.isConnected
				? mySubmissionConnection.submissions.map((submission) =>
						message.datovaZpravaId
							? {
									button: getDisconnectButton(
										mySubmissionConnection.disconnect.disconnect,
										{
											submissionId: submission.id,
											datovaZpravaId: message.datovaZpravaId,
											datovaSchrankaId: dataBoxId,
										}
									),
									icon: getIconDefinition('attachment', 'templates'),
								}
							: null
					)
				: [
						{
							button: getConnectButton(mySubmissionConnection.connect.connect, {
								datovaZpravaId: message.datovaZpravaId,
								datovaSchrankaId: dataBoxId,
							}),
							icon: getIconDefinition('attachment', 'templates'),
						},
					]
			: [];

	const copyToArchiveButton = {
		button: getButtonDefinition({
			color: 'primary',
			isLoading,
			isDisabled: isLoading,
			'aria-label': t('zpravy.zkopirovat-do-archivu'),
			onClick: handleArchiveMessage,
			children: t('zpravy.zkopirovat-do-archivu'),
		}),
		icon: getIconDefinition('archive', 'templates'),
	};

	const downloadMessageButton = message
		? {
				button: getButtonDefinition({
					color: 'primary',
					isLoading: loadingItems.download,
					isDisabled: loadingItems.download,
					onClick: () => handleDownloadMessage(message),
					'aria-label': t('zpravy.stahnout-zpravu'),
					children: t('zpravy.stahnout-zpravu'),
				}),
				icon: getIconDefinition('download', 'templates'),
			}
		: null;

	const deleteArchivedCopyButton = {
		button: getButtonDefinition({
			color: 'error',
			isLoading: loadingItems.remove,
			onClick: () => setConfirmModal(DataBoxConfirmModalType.removeMessage),
			isDisabled: isArchivingEnabled,
			'aria-label': t('zpravy.smazat-kopii'),
			children: t('zpravy.smazat-kopii'),
		}),
		icon: getIconDefinition('trash', 'templates'),
	};

	const deleteMessageButton = {
		button: getButtonDefinition({
			color: 'error',
			onClick: () =>
				setConfirmModal(DataBoxConfirmModalType.removeMessagesPermanently),
			'aria-label': t('zpravy.trvale-smazat'),
			children: t('zpravy.trvale-smazat'),
		}),
		icon: getIconDefinition('trash', 'templates'),
	};

	const restoreMessageButton = {
		button: getButtonDefinition({
			color: 'primary',
			isLoading: loadingItems.restore,
			onClick: () => handleRestoreMessage(redirect),
			isDisabled: !!loadingItems.restore,
			'aria-label': t('zpravy.obnovit'),
			children: t('zpravy.obnovit'),
		}),
		icon: getIconDefinition('arrow-counterclockwise', 'templates'),
	};

	const getCopyToArchiveButton = () => {
		if (!extraConfig.pageRoutes.databoxConfig.displayArchiveAction) {
			return null;
		}

		if (isDataBoxTypePO(dataBox) || message?.datumArchivace) {
			return null;
		}

		return copyToArchiveButton;
	};

	switch (folderType) {
		case DataBoxFolderType.Received:
			return (
				<ButtonDropdown
					buttons={[
						getCopyToArchiveButton(),
						...AnotherSubmissionButtons,
						downloadMessageButton,
					]}
				/>
			);
		case DataBoxFolderType.Sent:
			return (
				<ButtonDropdown
					buttons={[
						getCopyToArchiveButton(),
						...AnotherSubmissionButtons,
						downloadMessageButton,
					]}
				/>
			);
		case DataBoxFolderType.Archived:
			return (
				<ButtonDropdown
					buttons={[
						deleteArchivedCopyButton,
						...AnotherSubmissionButtons,
						downloadMessageButton,
					]}
				/>
			);
		case DataBoxFolderType.Trash:
			return (
				<ButtonDropdown buttons={[deleteMessageButton, restoreMessageButton]} />
			);
	}
};
